import { useEffect } from 'react';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

import { GlintsTrackingClient } from './GlintsTrackingClient';
import { getGlintsTrackingSyncConfig } from './initializeGlintsTrackingClient';
import { getEventQueue, setEventQueue } from './trackEvent';
import { trackClickEvent } from './tracking';
import {
  getGlintsTrackingIDFromCookie,
  setGlintsTrackingIDToCookie,
} from './trackingSessionUtils';
import useGlintsTracking from './useGlintsTracking';
import useTrackElementImpression from './useTrackElementImpression';

const EVENT_RETENTION_HOURS = 24;

export default function RenderlessInitializeGlintsTracking(): null {
  const trackEventRef = useGlintsTracking();
  // purge older events
  useEffect(() => {
    const purgeEvents = () => {
      const queue = getEventQueue();
      const cutoffTime = dayjs()
        .subtract(EVENT_RETENTION_HOURS, 'hour')
        .valueOf();

      const filteredQueue = queue.filter(
        event => event.event_time > cutoffTime
      );

      if (filteredQueue.length !== queue.length) {
        setEventQueue(filteredQueue);
      }
    };

    purgeEvents();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        purgeEvents();
      }
    });

    return () => {
      document.removeEventListener('visibilitychange', purgeEvents);
    };
  }, []);

  useEffect(() => {
    let sendInterval: ReturnType<typeof setInterval> | null = null;
    const syncEvents = async () => {
      const cookieTrackingID = getGlintsTrackingIDFromCookie();
      setGlintsTrackingIDToCookie(cookieTrackingID ?? uuidv4());
      await trackEventRef.current();
    };
    const startSendingEvents = () => {
      sendInterval = setInterval(
        syncEvents,
        getGlintsTrackingSyncConfig().syncIntervalInMs
      );
    };

    startSendingEvents();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startSendingEvents();
      } else {
        // if its going to the background just sync the events, this will prevent the events from being lost
        syncEvents();
        if (sendInterval) {
          clearInterval(sendInterval);
          sendInterval = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (sendInterval) {
        clearInterval(sendInterval);
        sendInterval = null;
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    // useRef is mutable and wont trigger a rerender,
    //  for some reason the linter is warning us here when it shouldn't
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const client = GlintsTrackingClient.getInstance();
    const listener = () => {
      trackEventRef.current();
    };
    const removeListener = client.addFlushListener(listener);
    return () => {
      removeListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof HTMLElement) {
        trackClickEvent(target);
      }
    };
    window.addEventListener('click', handler, true);
    return () => {
      window.removeEventListener('click', handler, true);
    };
  }, []);

  useTrackElementImpression();

  return null;
}
